import React from "react";
import { IAuthenticationState, IUser } from "../../App/reducer";
import { IClass, ICourse, ICourseInstance, IScormPackage, RequestState } from "../../App/types/common";
import Forbidden from "../pages/Forbidden";
import { SideMenu } from "../utils/SideMenu/SideMenu.container";
import { SideMenuArrowContext, SideMenuCloseArrow } from "../utils/SideMenu/SideMenuCloseArrow";
import Spinner from "../utils/Spinner";
import SubTopbar from "../utils/SubTopbar";
import TopBar from "../utils/TopBar";
import { DashboardPage } from "./DashboardPage";

export interface IDashboardStateProps {
  allCourseIdsOnDashboard: string[];
  accountId: string;
  editableCourses: string[];
  failedCoursesIds: string[];
  fetchAllDashboardDataRequestState: RequestState;
  fetchAllDashboardDataRequestError: { message: string } | null;
  loadingMoreCoursesState: RequestState;
  classes: IClass[];
  courseInstances: ICourseInstance[];
  courses: ICourse[];
  scormPackages: IScormPackage[];
  organizationName: string;
  user: IUser;
  isAdmin: boolean;
  authenticationState: IAuthenticationState;
}

export interface IDashboardDispatchProps {
  mount: () => void;
  loadAllCourses: () => void;
  loadMoreCourses: () => void;
  searchCourses: (searchInput: string) => void;
  unmount: () => void;
}

export type IDashboardProps = IDashboardStateProps & IDashboardDispatchProps;

interface IDashboardState {
  menuExpanded: boolean;
}

export class DashboardComponent extends React.Component<IDashboardProps, IDashboardState> {
  public static contextType = SideMenuArrowContext;
  constructor(props: IDashboardProps, context: {}) {
    super(props, context);
    this.state = {
      menuExpanded: true,
    };
  }

  public componentDidMount() {
    this.props.mount();
  }

  public componentWillUnmount() {
    this.props.unmount();
  }
  public render() {
    const withMenu = (children: React.ReactNode) => (
      <>
        <TopBar
          fitContent={false}
          showToggleMenu={true}
          onToggleMenuClick={this.onToggleMenuClick}
          userName={this.props.user.displayName}
        />
        <SideMenuCloseArrow />
        <SideMenu
          expanded={this.state.menuExpanded}
          onClose={this.onToggleMenuClick}
          organizationName={organizationName}
          userName={user.displayName}
          isOpen={isOpen}
        />
        <SubTopbar withSidebar={true} isOpen={isOpen}>
          {children}
        </SubTopbar>
      </>
    );
    const {
      allCourseIdsOnDashboard,
      accountId: account,
      isAdmin,
      failedCoursesIds,
      fetchAllDashboardDataRequestState,
      fetchAllDashboardDataRequestError,
      courses,
      classes,
      courseInstances,
      editableCourses,
      organizationName,
      scormPackages,
      user,
      authenticationState: { isKnown, isAuthenticated },
    } = this.props;
    const { isOpen } = this.context;
    const loading = !isKnown || !isAdmin || fetchAllDashboardDataRequestState !== RequestState.SUCCEEDED;

    if (fetchAllDashboardDataRequestState === RequestState.FAILED) {
      if (fetchAllDashboardDataRequestError !== null) {
        return withMenu(<div>{fetchAllDashboardDataRequestError.message}</div>);
      }
      return withMenu(<div>Failed</div>);
    }

    if (loading) {
      return <Spinner />;
    }

    if (!isAuthenticated) {
      return <Forbidden />;
    }

    return withMenu(
      <DashboardPage
        allCourseIdsOnDashboard={allCourseIdsOnDashboard}
        accountId={account}
        editableCourses={editableCourses}
        courseInstances={courseInstances}
        courses={courses}
        classes={classes}
        failedCoursesIds={failedCoursesIds}
        loadingMoreCoursesState={this.props.loadingMoreCoursesState}
        loadAllCourses={this.props.loadAllCourses}
        loadMoreCourses={this.props.loadMoreCourses}
        scormPackages={scormPackages}
        searchCourses={this.props.searchCourses}
        userIsAuthor={this.userCanEdit}
      />
    );
  }

  private userCanEdit = (courseId: string): boolean => {
    return this.props.user.isAuthor && this.props.editableCourses.includes(courseId);
  };

  private onToggleMenuClick = () => {
    this.setState({ menuExpanded: !this.state.menuExpanded });
  };
}
